import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from '../../../ds/clsx';
import { formatInteger } from '@brimo/shared';
import { Banner, Button, Dialog, Progress } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { closeImportList, fetchImportErrors, type ImportSummary, type ListDetail } from '../api';
import { Eyebrow } from '../ui';
import { downloadImportErrors } from './importErrors';
import styles from './lists.module.css';

const POLL_MS = 3_000;

/**
 * The import behind an import list (D-241, no frame): file, imported at/by, the counts, the progress of a background
 * import, „Fehlerliste herunterladen" and „Liste abschließen" — the confirmation names the never-reached contacts that
 * are deleted.
 */
export function ImportCard({ list, summary }: { list: ListDetail; summary: ImportSummary }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = summary.status === 'pending' || summary.status === 'running';
  const purging = summary.closedAt !== null && summary.stagedCount > 0;

  useEffect(() => {
    if (!running && !purging) return;
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer, 'list', list.id] });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, purging, queryClient, list.id]);

  async function downloadErrors() {
    setError(null);
    try {
      const result = await fetchImportErrors(list.id);
      downloadImportErrors(summary.fileName, result.rows);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  async function close() {
    setPending(true);
    setError(null);
    try {
      await closeImportList(list.id);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
      setConfirming(false);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  const counts = summary.counts;
  const skipped = counts.blocked + counts.invalidPhone + counts.duplicateInFile;

  return (
    <section className={styles.sideCard} aria-label="Import">
      <div className={styles.importSummary}>
        <Eyebrow>Import</Eyebrow>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.importFacts}>
          <span>{summary.fileName}</span>
          <span>
            {summary.importedBy
              ? `${summary.importedAtLabel} · ${summary.importedBy}`
              : summary.importedAtLabel}
          </span>
        </div>
        {running ? (
          <Progress
            label={`${formatInteger(summary.processedRows)} von ${formatInteger(summary.totalRows)} Zeilen importiert`}
            value={
              summary.totalRows === 0 ? 0 : Math.round((summary.processedRows / summary.totalRows) * 100)
            }
            showValue
          />
        ) : null}
        {summary.status === 'failed' ? (
          <Banner tone="danger">{summary.failure ?? 'Der Import ist fehlgeschlagen.'}</Banner>
        ) : null}
        <div className={clsx(styles.importCounts, styles.importCountsSide)}>
          <Count label="Neu" value={counts.new} />
          <Count label="Bereits Kontakt" value={counts.existing} />
          <Count label="Übersprungen" value={skipped} />
          <Count label="Noch nicht übernommen" value={summary.stagedCount} />
        </div>
        {summary.closedAt ? (
          <p className={styles.dialogHint}>
            {`Abgeschlossen ${summary.closedAtLabel ?? ''}${summary.closedBy ? ` · ${summary.closedBy}` : ''}`}
            {purging
              ? ` · ${formatInteger(summary.stagedCount)} nie erreichte Kontakte werden gelöscht.`
              : ` · ${formatInteger(summary.purgedCount ?? 0)} nie erreichte Kontakte gelöscht.`}
          </p>
        ) : null}
        <div className={styles.importActions}>
          {summary.errorCount > 0 ? (
            <Button variant="secondary" size="small" onClick={() => void downloadErrors()}>
              Fehlerliste herunterladen
            </Button>
          ) : null}
          {summary.canClose ? (
            <Button variant="secondary" size="small" onClick={() => setConfirming(true)}>
              Liste abschließen
            </Button>
          ) : null}
        </div>
      </div>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        tone="danger"
        title="Liste abschließen?"
        width={480}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={pending} onClick={() => void close()}>
              Liste abschließen
            </Button>
          </>
        }
      >
        <p className={styles.dialogHint}>
          {`${formatInteger(summary.stagedCount)} nie erreichte Kontakte werden endgültig gelöscht. Übernommene Kontakte bleiben erhalten.`}
        </p>
      </Dialog>
    </section>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div className={styles.importCount}>
      <span className={styles.importCountValue}>{formatInteger(value)}</span>
      <span className={styles.importCountLabel}>{label}</span>
    </div>
  );
}
